import { ENTER, COMMA } from "@angular/cdk/keycodes";
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { FormControl } from "@angular/forms";
import { MatAutocompleteSelectedEvent } from "@angular/material/autocomplete";
import { MatChipInputEvent } from "@angular/material/chips";
import { Store } from "@ngrx/store";
import { CompanyService, Pagination } from "blg-akaun-ts-lib";
import { PermissionStates } from "../../modules/permission/permission-controller/root";
import { UserPermInquirySelectors } from "../../modules/permission/user-permissions-inquiry-controller/selectors";
import { SearchStates } from "../../modules/search/search-controller/states";
import { AppConfig } from "../../visa";
import { Observable } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  map,
} from "rxjs/operators";
import { SubSink } from "subsink2";

@Component({
  selector: "app-company",
  templateUrl: "./company.component.html",
  styleUrls: ["./company.component.css"],
})
export class CompanyComponent implements OnInit {
  @Input() companyControl = new FormControl();
  @Output() selectionChange = new EventEmitter();
  @Input() viewModelStore;
  @Input() ColumnViewActions;
  @Input() ColumnViewSelectors;
  @Input() readPermissionDefintion;

  userPermissionTarget$ = this.permissionStore.select(
    UserPermInquirySelectors.selectUserPermInquiry
  );

  private subs = new SubSink();
  separatorKeysCodes: number[] = [ENTER, COMMA];
  filteredCompanies$: Observable<any[]>;
  companies: any[] = [];
  allCompanies: any[] = [];
  toppingsControl = new FormControl([]);

  @ViewChild("companyInput") companyInput: ElementRef<HTMLInputElement>;
  companyTargets: string[] = [];

  constructor(
    private companyService: CompanyService,
    private readonly store: Store<SearchStates>,
    private readonly permissionStore: Store<PermissionStates>
  ) {}

  ngOnInit(): void {
    this.viewModelStore
      .select(this.ColumnViewSelectors.selectAdvanceSearch_Company_Field)
      .subscribe((data) => {
        this.companies = data;
      });

    this.companyControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((data) => this.filterCompany(data));

    this.subs.sink = this.userPermissionTarget$.subscribe((targets) => {
      let target = targets.filter(
        (target) => target.permDfn === this.readPermissionDefintion
      );

      this.companyTargets =
        target[0]?.target !== null &&
        Object.keys(target[0]?.target || {}).length !== 0
          ? target[0]?.target["bl_fi_mst_comp"]
          : [];
    });
  }

  add(event: MatChipInputEvent): void {
    const value = (event.value || "").trim();
    // Add our company
    if (value) {
      this.companies.push(value);
    }

    // Clear the input value
    event.input.value = "";

    this.companyControl.setValue(null);

    this.viewModelStore.dispatch(
      this.ColumnViewActions.setAdvanceSearch_CompanyField({
        companies: this.companies,
      })
    );
  }

  remove(company: string): void {
    const index = this.companies.indexOf(company);

    if (index >= 0) {
      this.companies.splice(index, 1);
    }
    this.viewModelStore.dispatch(
      this.ColumnViewActions.setAdvanceSearch_CompanyField({
        companies: this.companies,
      })
    );
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    console.log(event.option);
    this.companies.push(event.option.value);
    this.companyInput.nativeElement.value = "";
    this.companyControl.setValue(null);
  }

  /**
   * Company autocomplete for the advanced-search chip list.
   *
   * Replaces the raw SELECT that went through SubQueryService:
   *
   *   FROM bl_fi_mst_comp hdr
   *   WHERE (hdr.name ILIKE '%x%' OR hdr.code ILIKE '%x%')
   *     AND hdr.status = 'ACTIVE'
   *     [AND hdr.guid IN (<permission targets>)]
   *   LIMIT 10
   *
   * CompanyUow.generateWhereClauseByCriteria (behind the companies query endpoint)
   * honours every part as criteria: `search_word` (bound, ILIKE over code, name and
   * comp_registration_num), `status`, and `hdr_guids` for the permission-target
   * scoping. The LIMIT 10 becomes the pagination limit.
   *
   * Behaviour notes:
   * - `search_word` also matches comp_registration_num.
   * - The permission-target scoping is preserved exactly: when companyTargets is
   *   empty no guid criterion is sent, as before.
   * - One request now instead of a subquery plus one getByGuid per hit.
   */
  filterCompany(search: string) {
    const criteria: any[] = [
      { columnName: 'status', operator: '=', value: 'ACTIVE' },
      { columnName: 'search_word', operator: '=', value: (search || '').trim() }
    ];
    if (this.companyTargets.length > 0) {
      criteria.push({ columnName: 'hdr_guids', operator: '=', value: this.companyTargets.toString() });
    }
    this.filteredCompanies$ = this.companyService
      .getByCriteria(new Pagination(0, 10, criteria), AppConfig.apiVisa)
      .pipe(
        map((response) =>
          response.data.map((container: any) => ({
            guid: container.bl_fi_mst_comp.guid,
            code: container.bl_fi_mst_comp.code.substring(0, 12),
            name: container.bl_fi_mst_comp.name,
          }))
        )
      );
  }

  optionClicked(event: Event, company: any) {
    event.stopPropagation();
    this.toggleSelection(company);
  }

  toggleSelection(company: any) {
    console.log(company);
    company.selected = !company.selected;
    this.companyInput.nativeElement.value = "";
    if (company.selected) {
      this.companies.push(company);
    } else {
      const i = this.companies.findIndex(
        (value) => value.guid === company.guid
      );
      this.companies.splice(i, 1);
    }
  }

  onReset() {
    this.companies = [];
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
